/** Select drivers and teams of drivers control */
import React, { useState, useEffect } from 'react';
import Select from '@vromo/ui/components/Select2';
import { observer } from 'mobx-react-lite';
import head from 'lodash/head';

import { useStore } from '@vromo/mobx-react';
import { Group, Job, Vehicle } from '@vromo/models';
import { closestTo } from '../../../stores/VehicleStore';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { LabelValue } from '@vromo/models/helpers/models';
import { FilterRow } from '@vromo/dashboard/src/blueprints/BlueprintStyles';
import { qaLabelDispatch } from './../../../../../../common-qa-labels/dispatch';

const SelectDriver = observer<{
  jobs: Job[],
  onSelect: (vehicles: Vehicle[]) => void,
  offer: boolean
}>(({ jobs, onSelect, offer }) => {

  const {
    vehicleStore,
    vehicleStore: { filteredVehicles, driverVehicleMap },
    groupStore,
    groupStore: { sortedGroups }
  } = useStore<RootStore>('rootStore');
  const [vehicleOptions, setVehicleOptions] = useState<LabelValue<string, number>[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<Group[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);

  const vehicleAsOption = (vehicle: Vehicle) => ({
    label: vehicle.displayName(driverVehicleMap, groupStore.withId(vehicle.group!)),
    value: vehicle.id!
  })
  const teamAsOption = (group: Group) => ({
    label: group.name || "Unamed team",
    value: group.id!
  })

  // update collection of available vehicles / default vehicle
  useEffect(() => {
    const availableVehicles = offer ? filteredVehicles.filter(({ synthetic }) => !synthetic) : filteredVehicles; 
    const closestVehiclesToJobs = closestTo(availableVehicles, jobs)
    setVehicleOptions(closestVehiclesToJobs.map(vehicleAsOption))
    if (availableVehicles.length > 0 && !offer) setSelectedVehicles([head(closestVehiclesToJobs)!])
    else setSelectedVehicles([])
  }, [filteredVehicles, offer]);

  // save the selected vehicles from the teams / vehicles controls
  useEffect(() => {
    const relatedVehicles: Vehicle[] = []
    selectedTeams.forEach(team => {
      const teamVehicles = filteredVehicles.filter(vehicle => vehicle.group! === team.id!)
      if (teamVehicles.length)
        relatedVehicles.push(...teamVehicles)
    })
    // avoid duplicates
    const selected = new Set([...relatedVehicles, ...selectedVehicles])
    onSelect(Array.from(selected))
  }, [selectedVehicles, selectedTeams, filteredVehicles, onSelect])

  const teamsWithNoSyntheticDrivers = sortedGroups.filter(({ id }) => !vehicleStore.hasSyntheticInGroup(id!)).map(teamAsOption)



  return (
    <>
      {
        offer &&
        <FilterRow>
          <label data-testid={qaLabelDispatch.assignJobModal.driversInTeamsDropdownLabel}>Drivers in Teams</label>
          <Select
            styles={{
              container: (styles) => ({
                ...styles,
                marginLeft: '10px',
                flex: 3
              })
            }}
            value={selectedTeams.map(teamAsOption)}
            onChange={(selection) => {
              if (!selection)
                setSelectedTeams([])
              else ("length" in selection)
              setSelectedTeams(selection.length ?
                selection.map(select => groupStore.withId(select.value)!) :
                []
              )
            }}
              options={teamsWithNoSyntheticDrivers}
            placeholder="Select a Team..."
            isClearable
            isMulti
          />
        </FilterRow>
      }

      <FilterRow>
        <label data-testid={qaLabelDispatch.assignJobModal.driversDropdownLabel}>Drivers</label>
        <Select
          id="drivers-select"
          styles={{
            container: (styles) => ({
              ...styles,
              marginLeft: '10px',
              flex: 3
            })
          }}
          value={selectedVehicles.map(vehicleAsOption)}
          onChange={selection => {
            if (!selection)
              setSelectedVehicles([])
            else if ("length" in selection)
              setSelectedVehicles(selection.length ?
                selection.map(select => vehicleStore.withId(select.value)!) :
                []
              )
            else
              setSelectedVehicles([vehicleStore.withId(selection!.value)!])
          }}
          options={vehicleOptions}
          placeholder="Select a Driver..."
          isClearable={offer}
          isMulti={offer}
        />
      </FilterRow>

    </>
  )
})

export default SelectDriver;
